import * as Sentry from "@sentry/react";
import { calculateExponentialBackoff } from "@theirstory/common/utils";
import axios from "axios";
import { throttle } from "lodash";

import { isAxiosError, nukeAuthHeaderAxiosReqTransform, sleep, uuidv4 } from "../components/utils/util";

import type {
  LocalRecordingStream,
  LocalRecordingStreamPartData,
  LocalRecordingStreamPartMetadata,
} from "./localdbNew";
import { recordingsDB } from "./localdbNew";
import logger from "./logger";

const UPLOAD_PROGRESS_DEBOUNCE = 2500;
const PART_UPLOAD_RETRY_LIMIT = 10;
const STREAM_UPLOAD_RETRY_LIMIT = 50;

// Unit is bytes
const S3_MIN_PART_SIZE = 2 ** 20 * 30; // 30MiB

const UNITIALIZABLE_UPLOAD = "UNITIALIZABLE_UPLOAD: Story likely deleted.";

class UnrecoverableError extends Error {
  constructor(...args: string[]) {
    super(...args);
    this.name = this.constructor.name;
  }
}

class UninitializableUploadError extends UnrecoverableError {
  constructor() {
    super(UNITIALIZABLE_UPLOAD);
    this.name = this.constructor.name;
  }
}

// TODO: utils

export function serializeAsyncFunction<T extends Array<any>>(func: (...args: T) => Promise<void>) {
  const queue: T[] = [];
  const status = { isRunning: false, flushing: false, promise: Promise.resolve() };

  const ret = (...args: T) => {
    if (status.flushing) {
      throw new Error(`Cannot invoke ${func.name} as it is currently being flushed`);
    }
    queue.push(args);
    console.log(`Added item #${queue.length} to the "${func.name}" queue`);
    if (!status.isRunning) {
      status.isRunning = true;
      status.promise = (async () => {
        try {
          let chunk: T;
          while (queue.length > 0) {
            chunk = queue.shift();
            await func(...chunk);
            console.log(`There are ${queue.length} items remaining in the "${func.name}" queue`);
          }
        } finally {
          status.isRunning = false;
        }
      })();
    }

    return status.promise;
  };

  ret.flush = async () => {
    status.flushing = true;
    try {
      return await status.promise;
    } catch {
      status.flushing = false;
    }
    return undefined;
  };

  return ret;
}

function debouncePromise<T extends Array<any>>(func: (...args: T) => Promise<void>) {
  const status = { isRunning: false, promise: Promise.resolve() };

  return (...args: T): Promise<void> => {
    if (!status.isRunning) {
      status.isRunning = true;
      status.promise = (async () => {
        try {
          return func(...args);
        } finally {
          status.isRunning = false;
        }
      })();
    } else {
      console.log(`debouncePromise called while ${func.name} was already running`);
    }

    return status.promise;
  };
}

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T;
type IDB = ThenArg<ReturnType<typeof recordingsDB>>;
type InitialLocalDbRecordingsData = Pick<
  LocalRecordingStream,
  "streamId" | "storyId" | "sessionId" | "localTimeOffset" | "userId" | "type"
>;
type LoggerTags = { [prop: string]: string };

class LocalMediaStorageContext {
  idb: IDB = null;
  loggerTags: LoggerTags = {};

  async ensureIDB() {
    if (!this.idb) {
      this.idb = await recordingsDB();
    }
  }

  trace(msg: string, storyId?: string) {
    logger.info(logger.headers.SESSION_TRACE, { msg, ...this.loggerTags, ...(storyId ? { storyId } : {}) });
    // console.log(msg);
  }

  setLoggerTags(tags: LoggerTags) {
    this.loggerTags = tags;
  }
}

export type LocalMediaRecorderCallbacks = {
  uploadSuccessCallback: SyncRecordingsCB;
  uploadFailureCallback: SyncFailureCB;
  allPartsInfoCallback: PartsInfoCB;
  partInfoCallback: PartInfoCB;
  partDoneRecordingCallback: PartStatusCB;
  partUploadSuccessCallback: PartStatusCB;
  partUploadFailureCallback: PartStatusMessageCB;
  partUploadProgressCallback: PartUploadProgressCB;
};

export class LocalMediaStream {
  ctx: LocalMediaStorageContext;
  id: string;
  streamId: string;
  storyId: string;
  sessionId: string;
  localTimeOffset: number;
  userId: string;
  recordingStartTime: number;
  type: string;
  uploadKey: string;
  uploadId: string;
  uploadToken: string;
  callbacks: LocalMediaRecorderCallbacks;

  private isDeleted = false;

  constructor(
    ctx: LocalMediaStorageContext,
    id: string,
    streamData: Omit<LocalRecordingStream, "id">,
    callbacks: LocalMediaRecorderCallbacks
  ) {
    this.ctx = ctx;
    this.id = id;
    this.streamId = streamData.streamId;
    this.storyId = streamData.storyId;
    this.sessionId = streamData.sessionId;
    this.localTimeOffset = streamData.localTimeOffset;
    this.userId = streamData.userId;
    this.recordingStartTime = streamData.recordingStartTime;
    this.type = streamData.type;
    this.callbacks = callbacks;
  }

  async initiateUpload(): Promise<boolean> {
    try {
      // We want this particular network request to use the user's regular auth token,
      // since this is the request that returns the media-specific upload token
      const result = await axios.post(`/stories/${this.storyId}/media/upload/new`, {
        type: this.type,
        streamId: this.streamId,
        recordingStart: this.recordingStartTime.toString(),
        localTimeOffset: this.localTimeOffset.toString(),
      });
      const { key, uploadId, token } = result.data;
      this.uploadKey = key;
      this.uploadId = uploadId;
      this.uploadToken = token;
      return true;
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response.status === 404) {
          // We have a special case if a story doesn't exist. In this case, there's no need
          // to upload the local recording and we simply move on to delete it.
          // (we also can't upload it anywhere as it would have no home :( )
          return false;
        }
      }
      throw err;
    }
  }

  async getPartUploadUrl(partNumber: number): Promise<string> {
    if (!(this.uploadId && this.uploadKey)) {
      await this.tryToInitiateUpload();
    }
    const result = await axios.get(`/stories/${this.storyId}/media/upload/next`, {
      headers: {
        authorization: this.uploadToken,
      },
      params: {
        uploadId: this.uploadId,
        key: this.uploadKey,
        partNumber: partNumber,
      },
    });
    const { partUrl } = result.data;
    return partUrl;
  }

  async completeUpload(): Promise<void> {
    await axios.post(
      `/stories/${this.storyId}/media/upload/completion`,
      {
        uploadId: this.uploadId,
        key: this.uploadKey,
        metadata: {
          streamId: this.streamId,
          sessionId: this.sessionId,
          recordingStart: this.recordingStartTime,
          recordingStop: (await this.getStream())?.recordingStopTime,
          localTimeOffset: this.localTimeOffset,
          mimeType: this.type,
        },
      },
      {
        headers: {
          authorization: this.uploadToken,
        },
      }
    );
    this.callbacks.uploadSuccessCallback(this);
    return;
  }

  async tryToInitiateUpload(reuploadAll = false): Promise<void> {
    const stream = await this.getStream();
    if ((!reuploadAll && this.uploadKey != null && this.uploadId != null) || stream == null) {
      return;
    }
    if (
      reuploadAll ||
      stream.uploadId == null ||
      stream.uploadKey == null ||
      stream.uploadToken == null ||
      (await this.getLastUpdateTime()) < new Date().getTime() - 60_000 * 60 * 24
    ) {
      // no upload has been initiated or the stop time was over a day ago
      const shouldAllowUpload = await this.initiateUpload();
      if (!shouldAllowUpload) {
        throw new UninitializableUploadError();
      }
      stream.uploadId = this.uploadId;
      stream.uploadKey = this.uploadKey;
      stream.uploadToken = this.uploadToken;

      // Set all parts to `uploaded: false` in a transaction
      const tx = this.ctx.idb.transaction(["streamPartMeta", "streams"], "readwrite");
      const store = tx.objectStore("streamPartMeta");
      for (const partId of stream.partIds) {
        const partMetadata = await store.get(partId);
        if (!partMetadata) {
          throw new UnrecoverableError(`Referenced partId=${partId} not found`);
        }
        partMetadata.uploaded = false;
        await store.put(partMetadata);
      }

      // Not sure if this is neccesary anymore?
      // Only place that could modify stream might be `initiateUpload`...
      await tx.objectStore("streams").put(stream);

      await tx.done;
    } else {
      // if we have already initiated the upload, use the existing id and key.
      this.uploadKey = stream.uploadKey;
      this.uploadId = stream.uploadId;
      this.uploadToken = stream.uploadToken;
    }
  }

  async getStream(): Promise<LocalRecordingStream> {
    return this.ctx.idb.get("streams", this.id);
  }

  async getPartMeta(partId: string): Promise<LocalRecordingStreamPartMetadata> {
    return this.ctx.idb.get("streamPartMeta", partId);
  }

  async getPartData(partId: string): Promise<LocalRecordingStreamPartData> {
    return this.ctx.idb.get("streamPartData", partId);
  }

  async putStream(stream: LocalRecordingStream): Promise<void> {
    await this.ctx.idb.put("streams", stream);
    this.updateLastUpdateTime();
  }

  async putPartMeta(partMeta: LocalRecordingStreamPartMetadata): Promise<void> {
    await this.ctx.idb.put("streamPartMeta", partMeta);
    this.updateLastUpdateTime();
  }

  async putPartData(partData: LocalRecordingStreamPartData): Promise<void> {
    await this.ctx.idb.put("streamPartData", partData);
    this.updateLastUpdateTime();
  }

  async getLastUpdateTime(): Promise<number> {
    return (await this.ctx.idb.get("streamUpdateTimes", this.streamId))?.updateTime || 0;
  }

  async updateLastUpdateTime(): Promise<void> {
    await this.ctx.idb.put("streamUpdateTimes", { streamId: this.streamId, updateTime: new Date().getTime() });
  }

  finish = debouncePromise(this._finish.bind(this));

  async _finish(fromRecorder = false, attempt = 0): Promise<void> {
    let stream: LocalRecordingStream;

    try {
      try {
        await this.tryToInitiateUpload(attempt > 0);
      } catch (e) {
        if (e instanceof Error && e.message === UNITIALIZABLE_UPLOAD) {
          console.warn(`Deleting stream (${this.id}) because its story (${this.storyId}) appears to have been deleted`);
          this.isDeleted = true;
          this.callbacks.uploadFailureCallback(this, `Story (${this.storyId}) was deleted`);
          await this.delete();
          return;
        } else {
          throw e;
        }
      }

      await this.pushChunk.flush();
      stream = await this.getStream();
      if (!stream) {
        this.isDeleted = true;
        this.callbacks.uploadFailureCallback(this, `Stream ${this.id} was corrupted`);
        await this.delete();
        throw new UnrecoverableError(`Stream ${this.id} doesn't exist...`);
      }
      // If we're getting this `finish` call from the media recorder as a result of a real "stop" event,
      // let's report that the stop time is now. Otherwise, if we're getting called from LocalMediaStorage
      // to trigger an upload of a previously-recorded stream, let's leave `recordingStopTime` alone
      if (fromRecorder) {
        stream.recordingStopTime = Date.now();
        await this.putStream(stream);
      }

      this.callbacks.allPartsInfoCallback(this, {
        parts: await Promise.all(stream.partIds.map((id) => this.getPartMeta(id))),
      });

      await this.uploadAllParts(stream);
      await this.completeUpload();
      console.log(`Finished uploading local recording ${this.id}. Deleting its local data`);
      await this.delete();

      // If we're still in the call and finish uploading a finished recording,
      // let's just directly tell the LocalMediaStorage to upload all other recordings
      // so that e.g other streams for this session get uploaded ASAP
      if (fromRecorder) {
        _LMS.syncRecordings();
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      this.callbacks.uploadFailureCallback(this, e.message || `${e}`);

      if (attempt < STREAM_UPLOAD_RETRY_LIMIT && !(e instanceof UnrecoverableError)) {
        const delay = calculateExponentialBackoff(attempt + 1);
        console.warn(
          `Failed to upload stream ${stream?.id || this.id}, because: ${e.message || e}. Retrying in ${delay}ms`
        );
        Sentry.captureException(e);
        await sleep(delay);
        await this._finish(fromRecorder, attempt + 1);
        return;
      } else {
        throw e;
      }
    }
  }

  async uploadAllParts(stream: LocalRecordingStream): Promise<void> {
    // this method's body is a deconstructued `reduce` on `recordingData.recordedArrayBuffers`
    const numParts = stream.partIds.length;
    // loop through all buffers and upload what hasn't been uploaded
    for (let partNum = 0; partNum < numParts; partNum++) {
      const partMeta = await this.getPartMeta(stream.partIds[partNum]);
      if (!partMeta) {
        throw new UnrecoverableError(
          `Missing part metadata for part #${partNum} (id=${stream.partIds[partNum]}) on stream ${stream.id}`
        );
      } else if (!partMeta.uploaded) {
        // only allow one upload at a time
        await this.uploadPartNumber(partNum);
      }
    }
  }

  uploadPartNumber = serializeAsyncFunction(this._uploadPartNumber.bind(this));

  async _uploadPartNumber(index: number, attempt = 0): Promise<void> {
    const stream = (await this.getStream()) || null;
    const partId = stream.partIds[index];
    const partMeta = await this.getPartMeta(partId);
    if (partMeta.uploaded) {
      return;
    }
    this.callbacks.partInfoCallback(this, partMeta, false);

    try {
      const partUrl = await this.getPartUploadUrl(index + 1);
      this.ctx.trace(`UPLOADING LOCAL RECORDING FILE(${this.id}) ${partUrl}; Key=${this.uploadKey}`, this.storyId);

      const partData = await this.getPartData(partId);

      const throttledProgressCallback = throttle(
        (evt) => {
          this.callbacks.partUploadProgressCallback(this, evt);
          this.updateLastUpdateTime();
        },
        UPLOAD_PROGRESS_DEBOUNCE,
        { leading: true }
      );

      await axios.create().put(partUrl, partData.arrayBuffer, {
        transformRequest: [
          nukeAuthHeaderAxiosReqTransform,
          (data, headers) => {
            delete headers.put["Content-Type"];
            return data;
          },
        ],
        onUploadProgress: (event: ProgressEvent) => {
          throttledProgressCallback({
            partId,
            // apparently event.loaded isn't bytes, it's a unitless ratio.
            // Anecdotally, this appears to be ~15-20% off from the real value,
            // so it's probably in something like KiB instead of KB
            // https://developer.mozilla.org/en-US/docs/Web/API/ProgressEvent/loaded
            // The docs have this to say:
            //  A 64-bit unsigned integer value indicating the amount of work
            //  already performed by the underlying process. The ratio of work done
            //  can be calculated by dividing total by the value of this property.
            //  When downloading a resource using HTTP, this only counts the body of
            // the HTTP message, and doesn't include headers and other overhead.
            uploadedBytes: (event.loaded / event.total) * partData.arrayBuffer.byteLength,
          });
        },
      });
      throttledProgressCallback.flush();
    } catch (e) {
      this.callbacks.partUploadFailureCallback(this, partId, e.message || `${e}`);
      if (e instanceof UninitializableUploadError) {
        await this.finish();
        return;
      }
      if (attempt < PART_UPLOAD_RETRY_LIMIT && !(e instanceof UnrecoverableError)) {
        const delay = calculateExponentialBackoff(attempt + 1);
        console.warn(`Failed to upload part ${partId}, because: ${e.message || e}. Retrying in ${delay}ms`);
        Sentry.captureException(e);
        await sleep(delay);
        await this._uploadPartNumber(index, attempt + 1);
        return;
      } else {
        throw e;
      }
    }
    // grab the recording again in case there have been writes since the upload began.
    const partMetaAfterUpload = await this.getPartMeta(partId);
    partMetaAfterUpload.uploaded = true;
    await this.ctx.idb.put("streamPartMeta", partMetaAfterUpload);
    this.callbacks.partUploadSuccessCallback(this, partId);
  }

  pushChunk = serializeAsyncFunction(this._pushChunk.bind(this));

  private async _pushChunk(event: BlobEvent) {
    if (this.isDeleted) {
      console.warn(`Ignoring chunk data because story (${this.storyId}) has been deleted`);
      return;
    }
    this.type = event.data.type;
    let stream = (await this.getStream()) || null;

    let lastRecordedPartData: LocalRecordingStreamPartData;
    let lastRecordedPartMetadata: LocalRecordingStreamPartMetadata;
    let lastPartId: string;
    if (stream != null) {
      lastPartId = stream.partIds[stream.partIds.length - 1];
      lastRecordedPartData = await this.getPartData(lastPartId);
      lastRecordedPartMetadata = await this.getPartMeta(lastPartId);
      // Normally we'd want this to only get set on media recorder "stop" events, but
      // in certain cases such as when a user kills the tab in the middle of a recording,
      // no "stop" event will occur, so it's better to have some value rather than null for this
      stream.recordingStopTime = Date.now();
    } else {
      lastPartId = uuidv4();
      lastRecordedPartMetadata = { byteLength: 0, uploaded: false, partId: lastPartId, streamId: this.id };
      lastRecordedPartData = { arrayBuffer: new ArrayBuffer(0), partId: lastPartId };

      stream = {
        storyId: this.storyId,
        streamId: this.streamId,
        sessionId: this.sessionId,
        uploadId: this.uploadId,
        uploadKey: this.uploadKey,
        uploadToken: this.uploadToken,
        recordingStartTime: this.recordingStartTime,
        recordingStopTime: null,
        localTimeOffset: this.localTimeOffset,
        userId: this.userId,
        type: this.type,
        id: this.id,
        partIds: [lastPartId],
      };
    }
    await this.putStream(stream);
    const blob = new Blob([...[lastRecordedPartData.arrayBuffer], event.data]);
    const arrayBuffer = await blob.arrayBuffer();
    // overwrite last buffer with new chunk
    lastRecordedPartData.arrayBuffer = arrayBuffer;
    lastRecordedPartMetadata.uploaded = false;
    lastRecordedPartMetadata.byteLength = arrayBuffer.byteLength;

    await Promise.all([this.putPartMeta(lastRecordedPartMetadata), this.putPartData(lastRecordedPartData)]);
    this.callbacks.partInfoCallback(this, lastRecordedPartMetadata, true);

    if (arrayBuffer.byteLength > S3_MIN_PART_SIZE) {
      // Save with a new empty buffer at the end and
      // upload the buffer with the most recent chunk that
      // pushed us over the minimum for multi-part uploads.

      const newPartId = uuidv4();
      await Promise.all([
        this.putPartMeta({ partId: newPartId, uploaded: false, byteLength: 0, streamId: stream.id }),
        this.putPartData({ partId: newPartId, arrayBuffer: new ArrayBuffer(0) }),
      ]);

      console.log(`Part size exceeded S3 minimum, splitting into part #${stream.partIds.length}`);

      stream.partIds.push(newPartId);

      this.callbacks.partDoneRecordingCallback(this, lastPartId);

      // We don't need to block `pushChunk` here, since this could take a while and
      // we'll just be queueing chunks in memory rather than writing them out to indexeddb
      // It _is_ possible that this will allow multiple chunks to be uploaded to S3 in parallel
      // Which we'll have to take care of by serializing `uploadPartNumber`
      void this.uploadPartNumber(stream.partIds.indexOf(lastPartId));

      await this.putStream(stream);
    }

    const parts = await Promise.all(stream.partIds.map((id) => this.getPartMeta(id)));
    this.callbacks.allPartsInfoCallback(this, { parts });
  }

  async delete(): Promise<void> {
    const tx = this.ctx.idb.transaction(
      ["streams", "streamPartMeta", "streamPartData", "streamUpdateTimes"],
      "readwrite"
    );
    const streamStore = tx.objectStore("streams");
    const streamPartMetaStore = tx.objectStore("streamPartMeta");
    const streamPartDataStore = tx.objectStore("streamPartData");
    this.ctx.trace(`Deleting ${this.id} from indexedDB`, this.storyId);

    const streamData = await streamStore.get(this.id);
    if (!streamData) {
      console.log(`Local recording ${this.id} does not exist/already deleted`);
      return;
    }
    const partIds = streamData.partIds;

    await streamStore.delete(this.id);
    await Promise.all(partIds.map((id) => streamPartMetaStore.delete(id)));
    await Promise.all(partIds.map((id) => streamPartDataStore.delete(id)));
    await tx.objectStore("streamUpdateTimes").delete(this.streamId);
    await tx.done;

    // Our sync implementation below pulls one doc at a time and if the doc isn't ready for
    // upload (i.e. it is presently being written to) it will stop. Once we delete a doc
    // we want to invoke a new sync in case there's anything else in the IDB that we should
    // upload
    void _LMS.syncRecordings();
  }
}

export class InMemoryLocalMediaStream extends LocalMediaStream {
  constructor(
    ctx: LocalMediaStorageContext,
    params: InitialLocalDbRecordingsData,
    callbacks: LocalMediaRecorderCallbacks
  ) {
    const now = Date.now();
    super(
      ctx,
      uuidv4(),
      {
        uploadId: null,
        uploadKey: null,
        uploadToken: null,
        recordingStartTime: now,
        recordingStopTime: null,
        partIds: [],
        ...params,
      },
      callbacks
    );
  }
}

type SyncRecordingsCB = (stream: LocalMediaStream) => void;
type SyncFailureCB = (stream: LocalMediaStream, reason: string) => void;
type PartStatusCB = (stream: LocalMediaStream, partId: string) => void;
type PartStatusMessageCB = (stream: LocalMediaStream, partId: string, message: string) => void;

export type PartsInfoEvent = { parts: LocalRecordingStreamPartMetadata[] };

export type PartUploadProgressEvent = {
  partsUploaded?: number; // Backwards-compatible...
  partBytes?: number; // Backwards-compatible...
  partId?: string; // Future-compatible...
  uploadedBytes: number;
};

type PartUploadProgressCB = (stream: LocalMediaStream, event: PartUploadProgressEvent) => void;
type PartsInfoCB = (stream: LocalMediaStream, event: PartsInfoEvent) => void;
type PartInfoCB = (stream: LocalMediaStream, part: LocalRecordingStreamPartMetadata, isRecording: boolean) => void;

const NoOp = () => {
  /* Default behavior: No action */
};

export class LocalMediaStorage {
  ctx = new LocalMediaStorageContext();
  _syncInProgress = false;
  pendingSyncCallback = NoOp as SyncRecordingsCB;
  preSyncCallback = NoOp as SyncRecordingsCB;
  uploadSuccessCallback = NoOp as SyncRecordingsCB;
  uploadFailureCallback = NoOp as SyncFailureCB;
  allPartsInfoCallback = NoOp as PartsInfoCB;
  partInfoCallback = NoOp as PartInfoCB;
  partDoneRecordingCallback = NoOp as PartStatusCB;
  partUploadSuccessCallback = NoOp as PartStatusCB;
  partUploadFailureCallback = NoOp as PartStatusMessageCB;
  partUploadProgressCallback = NoOp as PartUploadProgressCB;

  get callbacks(): LocalMediaRecorderCallbacks {
    return {
      allPartsInfoCallback: this.allPartsInfoCallback,
      partInfoCallback: this.partInfoCallback,
      uploadSuccessCallback: this.uploadSuccessCallback,
      uploadFailureCallback: this.uploadFailureCallback,
      partDoneRecordingCallback: this.partDoneRecordingCallback,
      partUploadSuccessCallback: this.partUploadSuccessCallback,
      partUploadFailureCallback: this.partUploadFailureCallback,
      partUploadProgressCallback: this.partUploadProgressCallback,
    };
  }

  async createStream({ streamId, storyId, sessionId, localTimeOffset, userId, type }: InitialLocalDbRecordingsData) {
    await this.ctx.ensureIDB();
    return new InMemoryLocalMediaStream(
      this.ctx,
      {
        streamId,
        storyId,
        sessionId,
        localTimeOffset,
        userId,
        type,
      },
      this.callbacks
    );
  }

  // It's safe to return all of the streams here since
  // they're not not the actual binary data anymore, just metadata
  async getStreams() {
    await this.ctx.ensureIDB();
    const recordings = await this.ctx.idb.getAll("streams", null, 1);
    return !recordings || recordings.length === 0 ? [] : recordings;
  }

  async syncRecordings() {
    if (this._syncInProgress) return;

    this._syncInProgress = true;
    this.ctx.trace("LocalMediaStorage media sync check - STARTING...");
    await this.ctx.ensureIDB();

    const streams = await this.getStreams();

    for (const dbStream of streams) {
      const stream = new LocalMediaStream(this.ctx, dbStream.id, dbStream, this.callbacks);
      if (new Date().getTime() - (await stream.getLastUpdateTime()) < 60_000) {
        this.pendingSyncCallback(stream);
        break;
      }

      this.preSyncCallback(stream);
      try {
        await stream.finish(dbStream);
      } catch (e) {
        if (!(e instanceof Error) || e.message !== UNITIALIZABLE_UPLOAD) {
          Sentry.captureException(e);
          throw e;
        }
      }
    }
    this.ctx.trace("LocalMediaStorage media sync check - FINISHED");
    this._syncInProgress = false;
  }
}

const _LMS = new LocalMediaStorage();

export default _LMS;
